import { useEffect } from 'react';
import Navbar from '../../../components/Navbar';
import Footer from '../../home/components/Footer';

export default function ElectricianMoorParkNorthwood() {
  useEffect(() => {
    window.scrollTo(0, 0);

    document.title = 'Electrician in Moor Park & Northwood | Beyond Electrical';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional electrician services in Moor Park and Northwood. NICEIC approved, 24/7 emergency callouts, free quotes. Trusted local electrical contractor.');
    }

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      (canonical as HTMLLinkElement).rel = 'canonical';
      document.head.appendChild(canonical);
    }
    (canonical as HTMLLinkElement).href = 'https://beyondelectrical.co.uk/electrician-in-moor-park-northwood/';
  }, []);

  return (
    <>
      <div className="min-h-screen bg-white">
        <Navbar />

        {/* Hero Section */}
        <section className="bg-[#0A1628] text-white pt-32 pb-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="max-w-4xl">
              <div className="flex flex-wrap gap-3 mb-6">
                <span className="px-4 py-2 bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-full text-[#FFD700] text-sm font-medium whitespace-nowrap">
                  NICEIC Approved
                </span>
                <span className="px-4 py-2 bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-full text-[#FFD700] text-sm font-medium whitespace-nowrap">
                  24/7 Emergency Service
                </span>
                <span className="px-4 py-2 bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-full text-[#FFD700] text-sm font-medium whitespace-nowrap">
                  Free Quotes
                </span>
              </div>

              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Electrician in <span className="text-[#FFD700]">Moor Park & Northwood</span>
              </h1>
              
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Professional electrician services in Moor Park and Northwood. NICEIC approved contractor providing installations, repairs, and safety testing. 24/7 emergency callouts available.
              </p>

              <div className="flex flex-wrap gap-4">
                <a 
                  href="tel:02031981818" 
                  className="px-8 py-4 bg-[#FFD700] text-[#0A1628] rounded-lg font-semibold hover:bg-[#FFD700]/90 transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-fill"></i>
                  Call 020 3198 1818
                </a>
                <a 
                  href="/contact" 
                  className="px-8 py-4 bg-white/10 text-white border-2 border-white/20 rounded-lg font-semibold hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Get Free Quote
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-[#0A1628] mb-4">
                Services in Moor Park & Northwood
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Comprehensive electrical services for homes and businesses
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-xl transition-all duration-300">
                <div className="text-5xl mb-4">⚡</div>
                <h3 className="text-2xl font-bold text-[#0A1628] mb-3">Electrical Installations</h3>
                <p className="text-gray-600 mb-4">Complete electrical installations including rewiring, consumer units, and new circuits.</p>
                <a href="/services/installations/electrical-installations" className="text-[#FFD700] font-semibold hover:underline cursor-pointer">
                  Learn more →
                </a>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-xl transition-all duration-300">
                <div className="text-5xl mb-4">🔧</div>
                <h3 className="text-2xl font-bold text-[#0A1628] mb-3">Electrical Repairs</h3>
                <p className="text-gray-600 mb-4">Fast and reliable repairs for all electrical faults and issues.</p>
                <a href="/services/repairs/electrical-repairs" className="text-[#FFD700] font-semibold hover:underline cursor-pointer">
                  Learn more →
                </a>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-xl transition-all duration-300">
                <div className="text-5xl mb-4">🚗</div>
                <h3 className="text-2xl font-bold text-[#0A1628] mb-3">EV Charger Installation</h3>
                <p className="text-gray-600 mb-4">Professional installation of electric vehicle charging points.</p>
                <a href="/services/installations/ev-charger" className="text-[#FFD700] font-semibold hover:underline cursor-pointer">
                  Learn more →
                </a>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-xl transition-all duration-300">
                <div className="text-5xl mb-4">🏠</div>
                <h3 className="text-2xl font-bold text-[#0A1628] mb-3">Full & Partial Rewiring</h3>
                <p className="text-gray-600 mb-4">Complete or partial rewiring services for older properties.</p>
                <a href="/services/installations/rewiring" className="text-[#FFD700] font-semibold hover:underline cursor-pointer">
                  Learn more →
                </a>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-xl transition-all duration-300">
                <div className="text-5xl mb-4">📋</div>
                <h3 className="text-2xl font-bold text-[#0A1628] mb-3">EICR Testing</h3>
                <p className="text-gray-600 mb-4">Electrical Installation Condition Reports for landlords and homeowners.</p>
                <a href="/services/safety-compliance/eicr-testing" className="text-[#FFD700] font-semibold hover:underline cursor-pointer">
                  Learn more →
                </a>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-xl transition-all duration-300">
                <div className="text-5xl mb-4">🚨</div>
                <h3 className="text-2xl font-bold text-[#0A1628] mb-3">Emergency Callouts</h3>
                <p className="text-gray-600 mb-4">24/7 emergency electrical services when you need them most.</p>
                <a href="/services/repairs/emergency-callouts" className="text-[#FFD700] font-semibold hover:underline cursor-pointer">
                  Learn more →
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-20 bg-[#0A1628] text-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                Why Choose Beyond Electrical?
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Your trusted local electrician in Moor Park and Northwood
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="bg-white/5 border border-[#FFD700]/30 rounded-xl p-8 text-center hover:bg-white/10 transition-all duration-300">
                <div className="text-5xl mb-4">✅</div>
                <h3 className="text-xl font-bold mb-3">NICEIC Approved</h3>
                <p className="text-gray-300">Fully qualified and certified electrical contractor</p>
              </div>

              <div className="bg-white/5 border border-[#FFD700]/30 rounded-xl p-8 text-center hover:bg-white/10 transition-all duration-300">
                <div className="text-5xl mb-4">📍</div>
                <h3 className="text-xl font-bold mb-3">Local Service</h3>
                <p className="text-gray-300">Based locally, serving Moor Park and Northwood</p>
              </div>

              <div className="bg-white/5 border border-[#FFD700]/30 rounded-xl p-8 text-center hover:bg-white/10 transition-all duration-300">
                <div className="text-5xl mb-4">⏰</div>
                <h3 className="text-xl font-bold mb-3">24/7 Available</h3>
                <p className="text-gray-300">Emergency callouts available day and night</p>
              </div>

              <div className="bg-white/5 border border-[#FFD700]/30 rounded-xl p-8 text-center hover:bg-white/10 transition-all duration-300">
                <div className="text-5xl mb-4">💰</div>
                <h3 className="text-xl font-bold mb-3">Free Quotes</h3>
                <p className="text-gray-300">No obligation quotes for all work</p>
              </div>
            </div>
          </div>
        </section>

        {/* Areas Covered Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-[#0A1628] mb-4">
                Areas We Cover
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Serving Moor Park, Northwood and surrounding areas
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
              <span className="px-6 py-3 bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-full text-[#0A1628] font-semibold">
                Moor Park
              </span>
              <span className="px-6 py-3 bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-full text-[#0A1628] font-semibold">
                Northwood
              </span>
              <span className="px-6 py-3 bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-full text-[#0A1628] font-semibold">
                Northwood Hills
              </span>
              <span className="px-6 py-3 bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-full text-[#0A1628] font-semibold">
                Eastbury
              </span>
              <span className="px-6 py-3 bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-full text-[#0A1628] font-semibold">
                Sandy Lodge
              </span>
              <span className="px-6 py-3 bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-full text-[#0A1628] font-semibold">
                Ruislip
              </span>
              <span className="px-6 py-3 bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-full text-[#0A1628] font-semibold">
                Pinner
              </span>
              <span className="px-6 py-3 bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-full text-[#0A1628] font-semibold">
                Harrow
              </span>
            </div>
          </div>
        </section>

        {/* Bottom CTA Section */}
        <section className="py-20 bg-[#0A1628] text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Need an Electrician in Moor Park or Northwood?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Contact us today for a free quote or emergency callout
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a 
                href="tel:02031981818" 
                className="px-8 py-4 bg-[#FFD700] text-[#0A1628] rounded-lg font-semibold hover:bg-[#FFD700]/90 transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-fill"></i>
                Call 020 3198 1818
              </a>
              <a 
                href="/contact" 
                className="px-8 py-4 bg-white/10 text-white border-2 border-white/20 rounded-lg font-semibold hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Get Free Quote
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
